// src/pages/Loginpage.jsx
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom"; // Import Link
import { LockClosedIcon } from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import useAuthStore from "../store/useAuthStore";
import { useLocation, useNavigate } from "react-router-dom";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { axiosInstance } from "../lib/axiosInstance"; // Import axiosInstance

// --- Google Icon SVG (Example) ---
const GoogleIcon = () => (
  <svg className="w-5 h-5 mr-2" viewBox="0 0 48 48">
    <path fill="#EA4335" d="M24 9.5c3.9 0 6.7 1.6 8.3 3.1l6.3-6.3C34.9 2.7 30 .7 24 .7 14.8.7 7.2 6.3 4.4 14.3l7.3 5.7C13.3 13.1 18.2 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.3 24.5c0-1.6-.1-3.1-.4-4.6H24v8.8h12.5c-.5 2.8-2.2 5.2-4.9 6.9l7.3 5.7c4.3-3.9 6.8-9.7 6.8-16.8z"/>
    <path fill="#FBBC05" d="M11.7 28.1c-.4-1.2-.6-2.5-.6-3.9s.2-2.7.6-3.9l-7.3-5.7C2.7 18.1 1.7 21 1.7 24.2s1 6.1 4.7 9.8l7.3-5.9z"/>
    <path fill="#34A853" d="M24 47.3c6.5 0 11.9-2.1 15.8-5.7l-7.3-5.7c-2.1 1.4-4.9 2.3-8.5 2.3-5.9 0-10.9-3.9-12.7-9.2l-7.3 5.7C7.2 41 14.8 47.3 24 47.3z"/>
    <path fill="none" d="M0 0h48v48H0z"/>
  </svg>
);
// --- End Google Icon SVG ---


const Loginpage = () => {
  const [credential, setCredential] = useState({
    email: "",
    password: "",
  });
  const { loginBuilder, customerLogin, setGoogleAuth } = useAuthStore(); // Add setGoogleAuth
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false); // Specific loading for Google
  const [callbackLoading, setCallbackLoading] = useState(false); // Loading during callback processing
  const navigate = useNavigate();
  const location = useLocation();
  const userType =
    new URLSearchParams(location.search).get("type") || "builder";

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (userType === "builder") {
        await loginBuilder(credential);
      } else {
        await customerLogin(credential);
      }
    } catch (error) {
      console.error("Login failed:", error);
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCredential({ ...credential, [name]: value });
  };

  const handleGoogleLoginClick = async () => {
    setGoogleLoading(true);
    try {
      const response = await axiosInstance.get("/customer/google/url");
      if (response.data.success && response.data.url) {
        window.location.href = response.data.url;
      } else {
        toast.error("Could not get Google login URL.");
        setGoogleLoading(false);
      }
    } catch (error) {
      toast.error("Error initiating Google login.");
      console.error("Google URL fetch error:", error);
      setGoogleLoading(false);
    }
  };

  // --- Handle Google Callback ---
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get("code");

    if (code && userType === "customer") {
      const handleGoogleCallback = async (authCode) => {
        setCallbackLoading(true);
        try {
          const response = await axiosInstance.post("/customer/google/callback", {
            code: authCode,
          });

          if (response.data.success) {
            const { token, user: googleUser } = response.data;
            setGoogleAuth(googleUser, token);
             // --- CHANGE THIS LINE ---
             navigate('/customer/projectinvite', { replace: true }); // Redirect to customer dashboard
             // --- END CHANGE ---
          } else {
            toast.error(response.data.message || "Google login failed.");
          }
        } catch (error) {
          toast.error("Error during Google callback.");
          console.error("Google Callback Error:", error);
        } finally {
           setCallbackLoading(false);
           navigate(location.pathname, { replace: true }); // Clean URL after processing
        }
      };

      handleGoogleCallback(code);
    }
  }, [location.search, userType, navigate, setGoogleAuth]); // Add dependencies

  if (callbackLoading) {
      return (
          <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "linear-gradient(310deg, #555555 4%, white 38%)" }}>
              <div className="flex flex-col items-center bg-white p-10 rounded-lg shadow-xl">
                  <AiOutlineLoading3Quarters className="animate-spin text-4xl text-blue-500 mb-4" />
                  <p className="text-gray-600">Authenticating with Google...</p>
              </div>
          </div>
      );
  }


  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{ background: "linear-gradient(310deg, #555555 4%, white 38%)" }}
    >
      <div className="flex flex-col lg:flex-row w-full max-w-4xl p-2 py-5 border border-gray-300 bg-white rounded-lg shadow-2xl overflow-hidden">
        {/* Left Side */}
        <div className="flex flex-col justify-center w-full lg:w-2/3 p-8 lg:p-11">
          <div className="text-start">
            <img
              src="/assets/black logo.png"
              alt="Ducktail Logo"
              className="h-16 lg:h-20 mb-6"
            />
            <p className="text-gray-600 mt-4 text-sm lg:text-base">
              Ducktail - The Construction Platform connects companies with
              skilled professionals, offering reliable solutions for projects of
              all sizes. From renovations to large developments, we ensure
              quality and efficiency.
            </p>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="flex flex-col justify-center w-full lg:w-1/2 p-8 lg:p-10">
          <h2 className="text-2xl font-semibold text-center text-gray-800">
            LOGIN ({userType === "builder" ? "Builder" : "Customer"})
          </h2>
          <form className="mt-6" onSubmit={handleLogin}>
            {/* Email Input */}
            <div className="mb-4">
              <div className="relative">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3 pt-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-5 h-5 text-black"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z"
                    />
                  </svg>
                </span>
                <input
                  type="text"
                  name="email"
                  value={credential.email}
                  className="w-full px-10 py-2 mt-2 border rounded-lg bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-300"
                  style={{ backgroundColor: "#dddddd", borderRadius: "24px" }}
                  placeholder="Email ID"
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>
            {/* Password Input */}
            <div className="mb-1">
              <div className="relative">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3 pt-2">
                  <LockClosedIcon className="w-5 h-5 text-black" />
                </span>
                <input
                  type="password"
                  name="password"
                  value={credential.password}
                  className="w-full px-10 py-2 mt-2 border rounded-lg bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-300"
                  style={{ backgroundColor: "#dddddd", borderRadius: "24px" }}
                  placeholder="Password"
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>
            {/* Forgot Password Link (Conditional) */}
            {userType === "builder" && (
              <div className="flex justify-end mb-4">
                <Link
                  to="/forgot-password"
                  className="text-sm text-blue-600 hover:underline"
                >
                  Forgot password?
                </Link>
              </div>
            )}
             {userType === "customer" && (
                <div className="flex justify-end mb-4">
                     <div className="h-5"></div> {/* Maintain spacing */}
                </div>
             )}


            {/* Login Button */}
            <div className="flex justify-center">
              <button
                type="submit"
                disabled={loading || googleLoading}
                className={`w-28 px-4 py-1 mt-4 text-black border-2 transition-all duration-300 flex items-center justify-center ${
                  (loading || googleLoading) ? "opacity-70 cursor-not-allowed" : ""
                }`}
                style={{
                  background: "linear-gradient(310deg, #dddddd 10%, white)",
                  borderRadius: "24px",
                }}
              >
                {loading ? (
                  <AiOutlineLoading3Quarters className="animate-spin text-xl" />
                ) : (
                  "LOGIN"
                )}
              </button>
            </div>

            {/* OR Separator (Only for Customer) */}
            {userType === "customer" && (
                <div className="flex items-center my-4">
                    <div className="flex-grow border-t border-gray-300"></div>
                    <span className="flex-shrink mx-4 text-gray-500 text-sm">OR</span>
                    <div className="flex-grow border-t border-gray-300"></div>
                </div>
            )}

            {/* Google Login Button (Only for Customer) */}
            {userType === "customer" && (
                <div className="flex justify-center">
                <button
                    type="button"
                    onClick={handleGoogleLoginClick}
                    disabled={loading || googleLoading}
                    className={`w-full max-w-xs px-4 py-2 border flex items-center justify-center gap-2 border-slate-200 rounded-lg text-slate-700 hover:border-slate-400 hover:text-slate-900 hover:shadow transition duration-150 ${
                    (loading || googleLoading) ? "opacity-70 cursor-not-allowed" : ""
                    }`}
                >
                    {googleLoading ? (
                    <AiOutlineLoading3Quarters className="animate-spin text-xl" />
                    ) : (
                        <>
                           <GoogleIcon />
                           <span>Login with Google</span>
                        </>
                    )}

                </button>
                </div>
            )}


            <p className="mt-4 text-center text-sm text-gray-800">
              Not registered yet?{" "}
              <Link
                to={
                  userType === "customer"
                    ? "/customer/signup"
                    : "/builder/signup"
                }
                className="text-blue-600 hover:underline"
              >
                Sign up
              </Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Loginpage;